import React, { useEffect, useState } from "react";
import axios from "axios";

const OrderList = ({
  refreshSignal,
  onSelectOrder,
  statusFilter = "",
  courierFilter = "",
}) => {
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(localStorage.getItem("username") || "");
  const [selectedCaller, setSelectedCaller] = useState(""); // Stores selected caller

  const [selectedMobile, setSelectedMobile] = useState("");
  const [mobileNumbers, setMobileNumbers] = useState([]); // Store unique mobile numbers
  const [filterMode, setFilterMode] = useState("staff"); // Default to staff filtering
  const [approvalStatus, setApprovalStatus] = useState("Pending"); // Default approval status filter

  const [filteredOrders, setFilteredOrders] = useState([]); 
  const [couriers, setCouriers] = useState([]); // Store couriers list
  const [selectedMobileFilter, setSelectedMobileFilter] = useState("");

  const [sentToCallerFilter, setSentToCallerFilter] = useState("checked"); // ✅ new filter for checkbox



  // ───────────────────────────────────────────────────────────────────────────
  // 1) Fetch couriers on mount
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchCouriers = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get("https://astrovidhya.in/api/couriers", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCouriers(response.data);
      } catch (err) {
        console.error("❌ Error fetching couriers:", err.response?.data || err.message);
      }
    };

    fetchCouriers();
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // 2) Set up “filterOrdersByMobile” + “clearOrderList” event listeners ONCE
  //    (No nested useEffect)
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleOrderFilter = (event) => {
      const { orders: filtered } = event.detail;
      console.log("✅ Displaying orders for this customer:", filtered);
      setFilteredOrders(filtered);
    };

    const handleClearOrderList = () => {
      console.log("⚠️ Clearing order list - No orders found.");
      setFilteredOrders([]);
    };

    window.addEventListener("filterOrdersByMobile", handleOrderFilter);
    window.addEventListener("clearOrderList", handleClearOrderList);

    return () => {
      window.removeEventListener("filterOrdersByMobile", handleOrderFilter);
      window.removeEventListener("clearOrderList", handleClearOrderList);
    };
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // 3) Debug effect for “selectedCaller” changes (SEPARATE from the event listener)
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    console.log("📌 Selected Caller:", selectedCaller);
    console.log("📌 Orders' assigned_to values:", orders.map((o) => o.assigned_to));
  }, [selectedCaller, orders]);

  // ───────────────────────────────────────────────────────────────────────────
  // 4) Another effect to handle auto-select mobile filter
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleAutoSelectMobileFilter = (event) => {
      const mobileNumber = event.detail.mobile;
      if (!mobileNumber) {
        console.warn("⚠️ No mobile number received for filtering.");
        return;
      }

      console.log("🚀 Auto-selecting mobile filter:", mobileNumber);
      setSelectedMobileFilter(mobileNumber);
      // Apply immediately:
      handleMobileDropdownChange({ target: { value: mobileNumber } });
    };

    window.addEventListener("filterOrdersByMobile", handleAutoSelectMobileFilter);
    return () => {
      window.removeEventListener("filterOrdersByMobile", handleAutoSelectMobileFilter);
    };
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // 5) Mobile dropdown onChange
  // ───────────────────────────────────────────────────────────────────────────
  const handleMobileDropdownChange = (event) => {
    const selectedMobile = event.target.value || "";
    console.log("📱 Mobile filter changed to:", selectedMobile);

    if (selectedMobile) {
      const filtered = orders.filter((order) => order.mobile === selectedMobile);
      setFilteredOrders(filtered);
    } else {
      setFilteredOrders([]);
    }
  };

  // ───────────────────────────────────────────────────────────────────────────
  // 6) Fetch orders → Filter them by logged-in user if not admin
  // ───────────────────────────────────────────────────────────────────────────
  const fetchOrders = async () => {
    const token = localStorage.getItem("authToken");
    const username = localStorage.getItem("username");

    try {
        const response = await axios.get("https://astrovidhya.in/api/orders", {
            headers: { Authorization: `Bearer ${token}` },
            params: { populate: 'courier assigned_to products.productId' } // ✅ Ensure related fields are populated
        });

        if (username === "admin") {
            // ✅ Admin sees all orders with populated fields
            setOrders(response.data);
        } else {
            // ✅ Fetch staff members to get the logged-in user's `_id`
            const staffResponse = await axios.get("https://astrovidhya.in/api/staff", {
                headers: { Authorization: `Bearer ${token}` }
            });

            const loggedInStaff = staffResponse.data.find(staff => staff.username === username);

            if (loggedInStaff) {
                const userId = loggedInStaff._id; // ✅ Get the actual staff ID
                const filteredOrders = response.data.filter(order => order.assigned_to === userId);
                setOrders(filteredOrders);
            } else {
                console.error("❌ No matching staff found for username:", username);
                setOrders([]); // No orders if no matching staff found
            }
        }
    } catch (error) {
        console.error("❌ Error fetching orders:", error);
    }
};

  

  // ───────────────────────────────────────────────────────────────────────────
  // 7) Listen for the filter event from OrderCreate.jsx
  //    (Though we already do above – but you had it repeated, so let's keep minimal)
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleMobileFilter = (event) => {
      const { mobile, orders: externalOrders } = event.detail;
      console.log("✅ Displaying orders for this customer:", externalOrders);
      setFilteredOrders(externalOrders);
      setSelectedMobileFilter(mobile);
    };

    const handleClearOrderList = () => {
      console.log("⚠️ Clearing order list - No orders found.");
      setFilteredOrders([]);
    };

    window.addEventListener("filterOrdersByMobile", handleMobileFilter);
    window.addEventListener("clearOrderList", handleClearOrderList);

    return () => {
      window.removeEventListener("filterOrdersByMobile", handleMobileFilter);
      window.removeEventListener("clearOrderList", handleClearOrderList);
    };
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // 8) Fetch staff members (optional) – you have logic to set default staff
  // ───────────────────────────────────────────────────────────────────────────
  const fetchStaffMembers = async () => {
    const token = localStorage.getItem("authToken");
    try {
      const response = await axios.get("https://astrovidhya.in/api/staff", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStaffMembers(response.data);
  
      // ✅ Get logged-in username
      const username = localStorage.getItem("username");
  
      // ✅ Find the logged-in staff member by username
      const loggedInStaff = response.data.find((staff) => staff.username === username);
  
      if (loggedInStaff) {
        setSelectedCaller(loggedInStaff._id); // ✅ Set the logged-in user as the default in the filter
      }
    } catch (err) {
      console.error("❌ Error fetching staff members:", err.response?.data || err.message);
    }
  };
  
  // ───────────────────────────────────────────────────────────────────────────
  // 9) On mount / refresh
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchOrders();
    fetchStaffMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal, statusFilter, courierFilter, approvalStatus]);

  // ───────────────────────────────────────────────────────────────────────────
  // 10) Another effect for switching filter mode on page load
  // ───────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    setFilterMode("staff"); 
    setSelectedMobile("");
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // 11) Handling row clicks
  // ───────────────────────────────────────────────────────────────────────────
  const handleRowClick = (order) => {
    const courierName = getCourierName(order.courier);
    const callerName =
      staffMembers.find((staff) => staff._id === order.assigned_to)?.name || "Unassigned";

    console.log("🚀 Dispatching selected order details:", {
      courierName,
      callerName,
      so: order.so,
    });

    // Dispatch an event with so
    window.dispatchEvent(
      new CustomEvent("orderSelected", {
        detail: { courierName, callerName, so: order.so },
      })
    );

    // Pass the full order object up
    onSelectOrder(order);
  };

 // ───────────────────────────────────────────────────────────────────────────
// 12) Filter logic: sent_to_caller + staff + mobile
// ───────────────────────────────────────────────────────────────────────────
useEffect(() => {
  let filtered = orders;

  // ✅ Filter by "sent_to_caller" checkbox logic
  if (sentToCallerFilter === "checked") {
    filtered = filtered.filter(order => order.sent_to_caller === true);
  } else if (sentToCallerFilter === "unchecked") {
    filtered = filtered.filter(order => order.sent_to_caller !== true); // includes false & undefined
  } // if "all", no filter applied

  // ✅ Filter by selected mobile number
  if (selectedMobileFilter) {
    filtered = filtered.filter(order => order.mobile === selectedMobileFilter);
  }

  // ✅ Filter by assigned caller (non-admin)
  if (selectedCaller) {
    filtered = filtered.filter(order => order.assigned_to === selectedCaller);
  }

  setFilteredOrders(filtered);
}, [orders, selectedMobileFilter, selectedCaller, sentToCallerFilter]);



  
  

  // ───────────────────────────────────────────────────────────────────────────
  // 13) Helper for courier name
  // ───────────────────────────────────────────────────────────────────────────
  const getCourierName = (courierId) => {
    const courier = couriers.find((c) => c._id === courierId);
    return courier ? courier.name : "No Courier Assigned";
  };

  // ───────────────────────────────────────────────────────────────────────────
  // RENDER
  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="order-list-container bg-white p-4 rounded-lg shadow-md">
      <h2 className="text-lg font-bold mb-4">Order List</h2>
      {error && <p className="text-red-500 mb-4">{error}</p>}

      <p className="text-sm text-gray-600 mb-2">
        🔹 <strong>Tip:</strong> Double-click an order to ensure the{" "}
        <strong>Caller</strong> and <strong>Courier</strong> names populate correctly.
      </p>

      {/* Approval Status Filter (hidden) */}
      <div className="hidden">
        <label className="font-medium">Filter by Approval Status:</label>
        <select
          name="approval_status"
          value={approvalStatus}
          onChange={(e) => setApprovalStatus(e.target.value)}
          className="border rounded px-2 py-1 ml-2"
        >
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
        </select>
      </div>

      <select
        name="cod_no_discount"
        value="cod_no_discount"
        onChange={() => {}}
        className="hidden"
      >
        <option value="show">Show COD + No Discount Orders</option>
      </select>


{/* shhhhh....*/}
{/* Sent to Caller Filter (hidden by CSS) */}
<div className="hidden">
  <label className="font-medium">Filter by Sent to Caller:</label>
  <select
    name="sent_to_caller"
    defaultValue="checked" // ✅ Default selection
    className="border rounded px-2 py-1 ml-2"
  >
    <option value="checked">Checked</option>
    <option value="unchecked">Unchecked</option>
  </select>
</div>



{/* shhhhh....*/}

      <div className="overflow-x-auto">
        {/* Hidden Mobile Filter Dropdown */}
        <select
          value={selectedMobileFilter}
          onChange={handleMobileDropdownChange}
          style={{ display: "none" }} 
        >
          <option value="">Show All Orders</option>
          {orders.map((order, index) => (
            <option key={index} value={order.mobile}>
              {order.mobile}
            </option>
          ))}
        </select>

        {/* Hidden Staff Filter Dropdown */}
        <select
          value={selectedStaff}
          onChange={(e) => setSelectedStaff(e.target.value)}
          className="hidden"
        >
          {staffMembers.map((staff) => (
            <option key={staff.name} value={staff.name}>
              {staff.name}
            </option>
          ))}
        </select>

  {/* Filter by Caller Dropdown */}
  <div className="mb-4">
  <label className="font-medium">Filter by:</label>
  <select
    value={selectedCaller} // ✅ Default selection is the logged-in user
    onChange={(e) => setSelectedCaller(e.target.value)}
    className="border rounded px-2 py-1 ml-2 hidden" // ✅ This hides the dropdown"
  >
    <option value="">All Callers</option>
    {staffMembers.map((staff) => (
      <option key={staff._id} value={staff._id}>
        {staff.name} {/* ✅ Display staff names */}
      </option>
    ))}
  </select>
</div>


        <table className="table-auto w-full border-collapse">
          <thead className="bg-gray-200">
            <tr>
              {[
                "#",
                "Order Date",
                "Order No.",
                "AWB No.",
                "Customer Name",
                "Mobile",
                "Courier",
                "Call Type",
                "Approval Status",
                "Order Time",
                "Caller",
                "Products",
                "Discount (₹)",
                "Total Amount",
                "Payment Method",
             //   "Sent to Caller", // ✅ Add this last
              ].map((header) => (
                <th
                  key={header}
                  className="px-4 py-2 text-left font-medium text-gray-700"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(filteredOrders.length > 0 ? filteredOrders : orders).map(
              (order, index) => (
                <tr
                  key={order._id}
                  onClick={() => handleRowClick(order)}
                  className="cursor-pointer hover:bg-gray-100"
                >
                  {/* Row Number */}
                  <td className="px-4 py-2">{index + 1}</td>

                  {/* Order Date */}
                  <td className="px-4 py-2">
                    {order.order_date
                      ? new Date(order.order_date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "N/A"}
                  </td>

                  {/* Order No. */}
                  <td className="px-4 py-2">{order.order_no || "N/A"}</td>

                  {/* AWB No. */}
                  <td className="px-4 py-2">{order.awb || "N/A"}</td>

                  {/* Customer Name */}
                  <td className="px-4 py-2">{order.customer_name || "N/A"}</td>

                  {/* Mobile */}
                  <td className="px-4 py-2">{order.mobile || "N/A"}</td>

                  {/* Courier */}
                  <td className="px-4 py-2">{getCourierName(order.courier)}</td>

                  {/* Call Type */}
                  <td className="px-4 py-2">{order.call_type || "Not Set"}</td>


                  {/* Approval Status */}
                  <td className="px-4 py-2">
                    {filteredOrders.length > 0
                      ? order.approval_status || "Pending"
                      : "Pending"}
                  </td>

                  {/* Order Time */}
                  <td className="px-4 py-2">{order.order_time || "N/A"}</td>

                  {/* Caller (Assigned Staff) */}
                  <td className="px-4 py-2">
                    {staffMembers.find((s) => s._id === order.assigned_to)?.name ||
                      "Unassigned"}
                  </td>

                  {/* Products */}
                  <td className="px-4 py-2">
                    {Array.isArray(order.products) && order.products.length > 0
                      ? order.products
                          .map((p, idx) => {
                            console.log(`🛠️ Debugging Product ${idx + 1}:`, p);
                            const productName = p.productName || "🚨 Unknown Product";
                            const price = p.price || 0;
                            return `${productName} (₹${price}) x${p.quantity}`;
                          })
                          .join(", ")
                      : "No Products"}
                  </td>

                  {/* Discount */}
                  <td className="px-4 py-2">{order.discount || 0}</td>

                  {/* Total Amount */}
                  <td className="px-4 py-2">₹{order.total_amount || 0}</td>

                  {/* Payment Method */}
                  <td className="px-4 py-2">{order.payment_method || "Not Specified"}</td>

                  {/* Sent to Caller 
<td className="px-4 py-2 text-center">
  {localStorage.getItem("username") === "admin" ? (
    <input
      type="checkbox"
      checked={order.sent_to_caller}
      onChange={async (e) => {
        const updatedValue = e.target.checked;
        try {
          await axios.put(`https://astrovidhya.in/api/orders/${order._id}`, {
            sent_to_caller: updatedValue,
          });
          setOrders((prev) =>
            prev.map((o) =>
              o._id === order._id ? { ...o, sent_to_caller: updatedValue } : o
            )
          );
        } catch (error) {
          console.error("❌ Error updating sent_to_caller:", error);
          alert("Failed to update checkbox.");
        }
      }}
    />
  ) : (
    order.sent_to_caller ? "✔️" : "❌"
  )}
</td>  */}

                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderList;
